import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, CheckCircle, XCircle, Loader2, Mail, Lock, User, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const isLoggedIn = !!localStorage.getItem('vocala_token');

  // Auth form state (for inline registration/login)
  const [authMode, setAuthMode] = useState('register'); // 'register' | 'login'
  const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/workspaces/invite-info/${token}`);
        setInfo(data);
        
        // Pre-fill email in registration form
        if (data.email) {
          setAuthForm(prev => ({ ...prev, email: data.email }));
        }
        
        if (data.status !== 'pending') {
          setError('This invite has already been used or expired.');
        }
      } catch (err) {
        setError('Invite not found or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const payload = authMode === 'login' 
        ? { email: authForm.email, password: authForm.password } 
        : authForm;
      
      const { data } = await api.post(endpoint, payload);
      
      if (data.access_token) {
        localStorage.setItem('vocala_token', data.access_token);
        if (data.workspace_id) {
          localStorage.setItem('vocala_workspace_id', data.workspace_id);
        }
        
        // Now accept the invite
        try {
          const acceptResult = await api.post(`/workspaces/accept-invite/${token}`);
          setAccepted(true);
          toast.success('🎉 Account created and workspace joined!');
          setTimeout(() => navigate('/'), 2000);
        } catch (inviteErr) {
          // Invite acceptance failed, but account was created
          toast.error('Account created, but invite may have expired. Contact your admin.');
          setTimeout(() => navigate('/'), 3000);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 
                  (authMode === 'login' ? 'Invalid credentials' : 'Registration failed');
      toast.error(msg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAcceptIfLoggedIn = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post(`/workspaces/accept-invite/${token}`);
      setAccepted(true);
      if (data.workspace_id) {
        localStorage.setItem('vocala_workspace_id', data.workspace_id);
      }
      toast.success('Workspace joined!');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to accept invite';
      if (err.response?.status === 410) {
        setError('Invitation has expired. Please request a new invite from your admin.');
      } else {
        setError(detail);
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" style={{ fontFamily: 'Georgia, serif' }}>
        <Loader2 className="w-8 h-8 text-[#2563eb] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center p-6" data-testid="invite-page" style={{ fontFamily: 'Georgia, serif' }}>
      <div className="absolute top-0 right-0 w-1/2 h-64 bg-gradient-to-bl from-[#2563eb]/5 to-transparent pointer-events-none" />
      
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1e40af] to-[#3b82f6] flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Users className="w-6 h-6 text-white" />
            </div>
            <span className="text-3xl font-semibold text-slate-800">
              Vocala<span className="text-[#2563eb]">.</span>
            </span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_8px_40px_rgba(15,23,42,0.08)]">
          {accepted ? (
            <div className="text-center" data-testid="invite-accepted">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Welcome aboard!</h2>
              <p className="text-slate-600 text-sm">You've joined <strong>{info?.workspace_name}</strong>. Redirecting to dashboard...</p>
            </div>
          ) : error ? (
            <div className="text-center" data-testid="invite-error">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Invitation Issue</h2>
              <p className="text-slate-600 text-sm mb-6">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="text-[#2563eb] hover:underline text-sm font-medium"
              >
                Go to Homepage
              </button>
            </div>
          ) : info && isLoggedIn ? (
            /* Already logged in - just accept */
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#2563eb]" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">Team Invitation</h2>
              <p className="text-slate-600 text-sm mb-2">
                <strong>{info.inviter_name}</strong> invited you to join
              </p>
              <p className="text-[#2563eb] font-semibold text-lg mb-1">{info.workspace_name}</p>
              <p className="text-slate-500 text-xs mb-6">as {info.role}</p>

              <button
                data-testid="accept-invite-btn"
                onClick={handleAcceptIfLoggedIn}
                disabled={accepting}
                className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold py-3.5 rounded-xl text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {accepting ? <><Loader2 className="w-4 h-4 animate-spin" /> Joining...</> : 'Accept & Join Workspace'}
              </button>
            </div>
          ) : info ? (
            /* Not logged in - show registration form */
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="w-7 h-7 text-[#2563eb]" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-1">You're Invited!</h2>
                <p className="text-slate-600 text-sm">
                  <strong>{info.inviter_name}</strong> invited you to join <strong className="text-[#2563eb]">{info.workspace_name}</strong>
                </p>
              </div>

              {/* Auth Mode Toggle */}
              <div className="flex bg-slate-50 rounded-xl p-1 mb-5">
                <button
                  onClick={() => setAuthMode('register')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                    authMode === 'register' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  Create Account
                </button>
                <button
                  onClick={() => setAuthMode('login')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                    authMode === 'login' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  Sign In
                </button>
              </div>

              <form onSubmit={handleAuthSubmit} className="space-y-3.5">
                {authMode === 'register' && (
                  <div>
                    <label className="text-slate-600 text-sm font-medium block mb-1.5">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={authForm.name}
                        onChange={e => setAuthForm({ ...authForm, name: e.target.value })}
                        required
                        className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 text-slate-800 text-sm focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none"
                        placeholder="Jane Smith"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-slate-600 text-sm font-medium block mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      value={authForm.email}
                      onChange={e => setAuthForm({ ...authForm, email: e.target.value })}
                      required
                      className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 text-slate-800 text-sm focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none"
                      placeholder={info.email || "you@company.com"}
                      readOnly={authMode === 'register' && info.email}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-slate-600 text-sm font-medium block mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={authForm.password}
                      onChange={e => setAuthForm({ ...authForm, password: e.target.value })}
                      required
                      minLength={6}
                      className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-11 py-3 text-slate-800 text-sm focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {authMode === 'register' && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs text-slate-700 leading-relaxed">
                      ✨ Creating an account will automatically accept the invitation and add you to <strong>{info.workspace_name}</strong>.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3.5 rounded-xl text-sm font-semibold transition-all shadow-md shadow-blue-500/20 flex items-center justify-center gap-2 mt-4 disabled:opacity-50"
                >
                  {authLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> {authMode === 'register' ? 'Creating Account...' : 'Signing In...'}</>
                  ) : (
                    <>{authMode === 'register' ? 'Create Account & Join' : 'Sign In & Join'} <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>

              <p className="text-center text-xs text-slate-500 mt-4">
                {authMode === 'register' ? 'Already have an account? ' : "Don't have an account? "}
                <button 
                  onClick={() => setAuthMode(authMode === 'register' ? 'login' : 'register')}
                  className="text-[#2563eb] hover:underline font-medium"
                >
                  {authMode === 'register' ? 'Sign In' : 'Create Account'}
                </button>
              </p>
            </div>
          ) : null}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} Vocala · A DS Technologies Product
        </p>
      </motion.div>
    </div>
  );
}
